/**
 * Offline Consolidated Review Builder — iter422
 *
 * Builds the same payload as the backend `/stocktake/multi-location/
 * sessions/{id}/consolidated-review` endpoint, but entirely from
 * IndexedDB (parent + each child's locally-saved calculation_snapshot).
 *
 * The aggregation math mirrors `routes/multi_location.py` so the
 * online and offline parent Calculate-&-Results panels show identical
 * numbers across every toggle state (Consolidated / single location /
 * subset).
 */
import offlineDb from './offlineDb';

const { STORES } = offlineDb;

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const round1 = (n) => Math.round((Number(n) || 0) * 10) / 10;

/**
 * Build a consolidated-review object from a parent session's
 * children stored in IndexedDB. Each child must have either a
 * `local_changes.calculation_snapshot` (computed via
 * calculateStockAuditLocally on the child wizard) or a server
 * snapshot pulled at download time.
 *
 * The returned shape matches the backend payload — `data.locations`,
 * `data.consolidated`, `data.completion`, `data.cellar`,
 * `data.outlet_aggregate`, `data.audit_mode`, `data.transfer_summary`.
 * `MultiLocationCalculateResultsPanel` is agnostic to source.
 */
export async function buildOfflineConsolidatedReview(parentSessionId) {
  const parentRow = await offlineDb.get(STORES.STOCKTAKE_PACKAGES, parentSessionId);
  if (!parentRow) throw new Error('Parent session not in offline storage');
  const parentPkg = parentRow.package || {};
  const parentSession = parentPkg.session || {};

  // Find every child row whose parent_session_id matches.
  const all = await offlineDb.getAll(STORES.STOCKTAKE_PACKAGES);
  const childRows = all.filter((row) => {
    const s = row?.package?.session;
    return s && s.parent_session_id === parentSessionId;
  });

  const locations = [];
  for (const row of childRows) {
    const cs = row.package?.session || {};
    const auditLoc = (row.package?.locations || []).find((l) => l.id === cs.audit_location_id);
    const locName = auditLoc?.name || row.session_reference || 'Location';
    const locType = auditLoc?.location_type || 'standard';
    // Pick the calc snapshot — prefer local (more recent) over server.
    const localSnap = row.local_changes?.calculation_snapshot;
    const serverSnap = row.package?.calculation_snapshot || null;
    const snap = localSnap || serverSnap;
    let calc = null;
    if (snap) {
      // Translate the offline-engine field names into the same flat
      // shape the backend consolidated-review returns.
      calc = {
        opening_stock_value: snap.opening_stock_value ?? 0,
        deliveries_value: snap.deliveries_value ?? 0,
        credits_value: snap.credits_value ?? 0,
        // iter597 — prefer 'sales_at_cost' (PLU COS); 'sales_value' historically
        // carried effective_sales_value (= transfers_out for cellar snapshots).
        sales_at_cost: snap.sales_at_cost ?? snap.sales_value ?? 0,
        sales_at_selling_excl_vat: snap.sales_at_selling_excl_vat ?? 0,
        transfers_in_value: snap.transfers_in_value ?? 0,
        transfers_out_value: snap.transfers_out_value ?? 0,
        adjustments_value: snap.adjustments_value ?? 0,
        management_value: snap.management_value ?? 0,
        allowance_value: snap.allowance_value ?? 0,
        wastage_value: snap.wastage_value ?? 0,
        management_sales_incl_vat: snap.management_sales_incl_vat ?? 0,
        management_sales_excl_vat: snap.management_sales_excl_vat ?? 0,
        management_cost_of_sales: snap.management_cost_of_sales ?? snap.management_value ?? 0,
        expected_closing_value: snap.expected_closing_value ?? 0,
        total_value: snap.actual_closing_value ?? snap.total_value ?? 0,
        stocktake_result: snap.stocktake_result ?? 0,
        line_count: snap.line_count ?? 0,
        accounts_sales_excl_vat: snap.accounts_sales_excl_vat ?? null,
        accounts_sales_incl_vat: snap.accounts_sales_incl_vat ?? null,
        estimated_receipts_excl_vat: snap.estimated_receipts_excl_vat ?? null,
        // iter423-fix — include the incl-VAT counterpart so the
        // outlet-only Yield % rollup works offline.
        estimated_receipts_incl_vat: snap.estimated_receipts_incl_vat ?? null,
        stocktake_result_legacy: snap.stocktake_result_legacy ?? snap.stocktake_result ?? 0,
        stocktake_result_revised: snap.stocktake_result_revised ?? null,
        stocktake_result_legacy_adjusted: snap.stocktake_result_legacy_adjusted ?? null,
        stocktake_result_revised_adjusted: snap.stocktake_result_revised_adjusted ?? null,
        headline_methodology: snap.headline_methodology || 'legacy',
        headline_stocktake_result: snap.headline_stocktake_result ?? snap.stocktake_result ?? 0,
        headline_fallback_reason: snap.headline_fallback_reason ?? null,
        // iter423 — operational benchmarks pulled through so subset
        // rollups can derive Stockholding etc. offline.
        cost_of_sales_indirect: snap.cost_of_sales_indirect ?? null,
        stockholding_days: snap.stockholding_days ?? null,
        period_days: snap.period_days ?? null,
        avg_daily_cos: snap.avg_daily_cos ?? null,
        yield_pct: snap.yield_pct ?? null,
        version: snap.version || 1,
        calculated_at: snap.calculated_at || row.local_changes?.updated_at || null,
        is_cellar_session: locType === 'cellar',
      };
    }
    locations.push({
      session_id: cs.id,
      session_reference: cs.session_reference,
      location_id: cs.audit_location_id,
      location_name: locName,
      location_type: locType,
      auditor_id: cs.auditor_id,
      auditor_name: cs.auditor_name || 'Unassigned',
      status: cs.status || 'draft',
      has_calculation: !!calc,
      calculation: calc,
    });
  }

  // Consolidated totals — every location summed (subset slicing is
  // done in the panel itself).
  const calculated = locations.filter((l) => l.has_calculation);
  const consolidated = aggregateLocationCalcs(calculated.map((l) => l.calculation));

  const auditMode = parentSession.audit_mode || 'multi_no_cellar';
  const cellarLoc = locations.find((l) => l.location_type === 'cellar' && l.calculation);
  const outletCalcs = locations
    .filter((l) => l.location_type !== 'cellar' && l.calculation)
    .map((l) => l.calculation);
  const outletAggregate = outletCalcs.length ? aggregateLocationCalcs(outletCalcs) : null;
  if (outletAggregate) outletAggregate.location_count = outletCalcs.length;

  const transfers = parentPkg.transfers || [];
  const postedTransfers = transfers.filter((t) => (t.status || '').toLowerCase() === 'posted');
  const draftTransfers = transfers.filter((t) => (t.status || '').toLowerCase() === 'draft');

  return {
    session_id: parentSession.id,
    parent_reference: parentSession.session_reference,
    session_date: parentSession.session_date,
    period_from: parentSession.period_from,
    period_to: parentSession.period_to,
    site_name: parentSession.site_name,
    audit_mode: auditMode,
    locations,
    consolidated,
    completion: {
      total_locations: locations.length,
      calculated: calculated.length,
      all_calculated: locations.length > 0 && calculated.length === locations.length,
    },
    cellar: cellarLoc || null,
    outlet_aggregate: outletAggregate,
    transfer_summary: {
      total_count: transfers.length,
      posted_count: postedTransfers.length,
      posted_value: round2(postedTransfers.reduce((s, t) => s + (Number(t.total_value) || 0), 0)),
      draft_count: draftTransfers.length,
      details: transfers.map((t) => ({
        id: t.id,
        from_location: t.from_location_name || 'Supplier',
        to_location: t.to_location_name || '?',
        status: t.status,
        transfer_date: t.transfer_date,
        total_value: Number(t.total_value) || 0,
      })),
    },
    _offline: true,
  };
}

/**
 * Roll up a list of per-location calculation objects into a single
 * consolidated object, mirroring the math in
 * `routes/multi_location.py::consolidated_review`.
 *
 * Exported so the panel can reuse it for subset-selection rollups
 * (e.g. "Bar 1 + Bar 2 but not Cellar"). The shape of the returned
 * object matches `data.consolidated` from the backend payload — same
 * keys, same rounding rules — so a single set of `ConsolidatedKpiTile`s
 * can render any selection without conditional rendering.
 */
export function aggregateLocationCalcs(calcs) {
  const sum = (key) => calcs.reduce((s, c) => s + (Number(c?.[key]) || 0), 0);

  const opening = sum('opening_stock_value');
  const deliveries = sum('deliveries_value');
  const credits = sum('credits_value');
  const sales = sum('sales_at_cost');
  const adjustments = sum('adjustments_value');
  const management = sum('management_value');
  const allowance = sum('allowance_value');
  const wastage = sum('wastage_value');
  const mgmtIncl = sum('management_sales_incl_vat');
  const mgmtExcl = sum('management_sales_excl_vat');
  const transfersIn = sum('transfers_in_value');
  const transfersOut = sum('transfers_out_value');
  const actual = sum('total_value');
  const lineCount = sum('line_count');

  // Outlets-only rollup for the revised methodology
  const outletCalcs = calcs.filter((c) => !c?.is_cellar_session);
  const sumOutlet = (key) => outletCalcs.reduce((s, c) => s + (Number(c?.[key]) || 0), 0);
  let accountsSalesExcl = sumOutlet('accounts_sales_excl_vat');
  let accountsSalesIncl = sumOutlet('accounts_sales_incl_vat');
  let estimatedReceiptsExcl = sumOutlet('estimated_receipts_excl_vat');
  let estimatedReceiptsIncl = sumOutlet('estimated_receipts_incl_vat');
  // iter423-fix — treat all-zero as "not entered" when no outlet
  // actually contributed a non-zero value. This mirrors the backend's
  // behaviour and stops the panel from showing a misleading "£0.00"
  // when the underlying value is null. Affects Estimated Receipts +
  // Actual Receipts when a single-location selection is made on a
  // cellar (or a fresh outlet whose Sales step is empty).
  const anyAccExcl = outletCalcs.some((c) => (c?.accounts_sales_excl_vat ?? null) != null && c.accounts_sales_excl_vat !== 0);
  if (accountsSalesExcl === 0 && !anyAccExcl) accountsSalesExcl = null;
  const anyAccIncl = outletCalcs.some((c) => (c?.accounts_sales_incl_vat ?? null) != null && c.accounts_sales_incl_vat !== 0);
  if (accountsSalesIncl === 0 && !anyAccIncl) accountsSalesIncl = null;
  const anyEstExcl = outletCalcs.some((c) => (c?.estimated_receipts_excl_vat ?? null) != null && c.estimated_receipts_excl_vat !== 0);
  if (estimatedReceiptsExcl === 0 && !anyEstExcl) estimatedReceiptsExcl = null;
  const anyEstIncl = outletCalcs.some((c) => (c?.estimated_receipts_incl_vat ?? null) != null && c.estimated_receipts_incl_vat !== 0);
  if (estimatedReceiptsIncl === 0 && !anyEstIncl) estimatedReceiptsIncl = null;

  // Expected & legacy result — see multi_location.py:1252-1262
  const expected = round2(opening + deliveries - credits - sales + management);
  const resultLegacy = round2(actual - expected);
  // Consumption at cost = Σ each location's engine figure (derived management cost is not added).
  const cosIndirect = calcs.length && calcs.every((c) => c?.cost_of_sales_indirect != null)
    ? round2(sum('cost_of_sales_indirect'))
    : round2(opening + deliveries + transfersIn - credits - transfersOut - actual);
  const grossVals = outletCalcs.map((c) => (c?.estimated_receipts_incl_vat_gross != null
    ? Number(c.estimated_receipts_incl_vat_gross)
    : (c?.estimated_receipts_incl_vat != null ? Number(c.estimated_receipts_incl_vat) + (Number(c?.allowance_value) || 0) : null)));
  const estimatedReceiptsInclGross = grossVals.some((g) => g != null)
    ? round2(grossVals.reduce((s, g) => s + (g || 0), 0)) : null;

  // Same basis as the engine: Actual Receipts (incl VAT) − Net Estimated Receipts (incl VAT).
  const resultRevised = (accountsSalesIncl != null && estimatedReceiptsIncl != null)
    ? round2(accountsSalesIncl - estimatedReceiptsIncl)
    : null;

  // iter423-fix-v2 — Consolidated adjusted results are now the SUM of
  // per-location adjusted results, not re-derived from rolled-up
  // totals. This mirrors the backend fix in routes/multi_location.py
  // and guarantees Consolidated = Σ(per-location) — which is what
  // users see when they click any individual location pill.
  const resultLegacyAdjusted = round2(
    calcs.reduce((s, c) => s + (Number(c?.stocktake_result_legacy_adjusted) || 0), 0)
  );
  const anyRevisedChild = calcs.some(c => c?.stocktake_result_revised_adjusted != null);
  const resultRevisedAdjusted = anyRevisedChild
    ? round2(calcs.reduce((s, c) => s + (Number(c?.stocktake_result_revised_adjusted) || 0), 0))
    : null;
  // S1 (2026-10, decided) — for Revised methodology WITH a physical
  // cellar in this selection, the receipts-based headline is
  // Σ(trading outlets) only. The cellar's own headline is forced to
  // legacy/cost by the engine; summing it into a receipts-basis total
  // silently mixed bases. Mirrors routes/multi_location.py exactly so
  // the online and offline/subset panels never diverge.
  const hasCellarCalc = calcs.some((c) => c?.is_cellar_session);
  const isRevisedMethod = calcs.some((c) => c?.headline_methodology === 'revised');
  const headlineSourceCalcs = (isRevisedMethod && hasCellarCalc) ? outletCalcs : calcs;
  const headlineSum = round2(
    headlineSourceCalcs.reduce((s, c) => s + (Number(c?.headline_stocktake_result) || 0), 0)
  );
  // The cellar's own physical variance, reported separately at cost —
  // never summed into the receipts-based headline above.
  const cellarCalc = calcs.find((c) => c?.is_cellar_session);
  const cellarPhysicalVarianceAtCost = cellarCalc
    ? (cellarCalc.stocktake_result_legacy_adjusted ?? cellarCalc.stocktake_result_legacy ?? null)
    : null;

  const salesAtSellingExcl = sumOutlet('sales_at_selling_excl_vat');
  const gpLegacy = outletCalcs.length
    ? round2(salesAtSellingExcl - sumOutlet('sales_at_cost'))
    : null;
  const gpPctLegacy = salesAtSellingExcl > 0 ? round2(gpLegacy / salesAtSellingExcl * 100) : null;
  const gpRevised = accountsSalesExcl != null ? round2(accountsSalesExcl - cosIndirect) : null;
  const gpPctRevised = (accountsSalesExcl && accountsSalesExcl > 0)
    ? round2(gpRevised / accountsSalesExcl * 100)
    : null;

  // iter423 — additional benchmarks that mirror what the backend's
  // /consolidated-review endpoint emits so the offline path can
  // populate Stockholding, Period Days etc.
  const periodDaysVals = calcs.map((c) => Number(c?.period_days || 0)).filter((v) => v > 0);
  const periodDays = periodDaysVals.length ? Math.max(...periodDaysVals) : null;
  const cosForDays = accountsSalesExcl != null ? cosIndirect : round2(sales);
  const avgDailyCos = (periodDays && periodDays > 0 && cosForDays > 0) ? round2(cosForDays / periodDays) : null;
  const stockholdingDays = (avgDailyCos && avgDailyCos > 0) ? Math.round(round2(actual) / avgDailyCos) : null;

  return {
    opening_stock_value: round2(opening),
    deliveries_value: round2(deliveries),
    credits_value: round2(credits),
    sales_at_cost: round2(sales),
    transfers_in_total: round2(transfersIn),
    transfers_out_total: round2(transfersOut),
    transfers_net: round2(transfersIn - transfersOut),
    adjustments_value: round2(adjustments),
    management_value: round2(management),
    allowance_value: round2(allowance),
    wastage_value: round2(wastage),
    management_sales_incl_vat: round2(mgmtIncl),
    management_sales_excl_vat: round2(mgmtExcl),
    management_cost_of_sales: round2(management),
    expected_closing_value: expected,
    total_value: round2(actual),
    line_count: lineCount,
    stocktake_result: resultLegacy,
    stocktake_result_legacy: resultLegacy,
    stocktake_result_revised: resultRevised,
    stocktake_result_legacy_adjusted: resultLegacyAdjusted,
    stocktake_result_revised_adjusted: resultRevisedAdjusted,
    // iter423-fix-v2 — sum of per-location headlines (single source of
    // truth; matches what users see when they click each location pill).
    headline_stocktake_result: headlineSum,
    accounts_sales_excl_vat: accountsSalesExcl,
    accounts_sales_incl_vat: accountsSalesIncl,
    estimated_receipts_excl_vat: estimatedReceiptsExcl,
    estimated_receipts_incl_vat: estimatedReceiptsIncl,
    estimated_receipts_incl_vat_gross: estimatedReceiptsInclGross,
    wastage_included_value: round2(sumOutlet('wastage_included_value')),
    cost_of_sales_indirect: cosIndirect,
    // Configured method comes from the locations (org setting); never inferred from data presence.
    headline_methodology: calcs.some((c) => c?.headline_methodology === 'revised') ? 'revised' : 'legacy',
    headline_fallback_reason: null,
    headline_gross_profit: gpRevised != null ? gpRevised : gpLegacy,
    headline_gp_pct: gpRevised != null ? gpPctRevised : gpPctLegacy,
    gross_profit_legacy: gpLegacy,
    gp_pct_legacy: gpPctLegacy,
    gross_profit_revised: gpRevised,
    gp_pct_revised: gpPctRevised,
    // Canonical Yield (kpi_engine): Actual ÷ (Gross Estimated Receipts + Management) × 100.
    yield_pct: (() => {
      if (estimatedReceiptsInclGross == null || accountsSalesIncl == null) return null;
      const denom = estimatedReceiptsInclGross + mgmtIncl;
      if (denom <= 0) return null;
      const pct = (accountsSalesIncl / denom) * 100;
      return pct <= 0 || pct > 200 ? null : round1(pct);
    })(),
    // iter423 — extra benchmarks for parity with single-loc panel.
    sales_at_selling_excl_vat: round2(salesAtSellingExcl),
    period_days: periodDays,
    avg_daily_cos: avgDailyCos,
    stockholding_days: stockholdingDays,
    // S1 (2026-10, decided) — cellar physical variance at cost, shown
    // separately, never folded into the receipts-based headline.
    cellar_physical_variance_at_cost: cellarPhysicalVarianceAtCost,
    cellar_physical_variance_excluded_from_headline: !!(hasCellarCalc && isRevisedMethod),
  };
}
